import { useState } from "react"

import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { Toaster } from "sonner"

import { QueryErrorBoundary } from "@/components/query-error-boundary"
import { SidebarProvider } from "@/components/ui/sidebar"
import { TooltipProvider } from "@/components/ui/tooltip"
import { RoutesProvider } from "@/contexts/routes-context"
import { MicrosoftAuthProvider } from "@/features/auth/components/microsoft-auth-provider"
import { HeavyPreviewLegacySeed } from "@/features/chat/components/heavy-preview-legacy-seed"
import { useFavicon } from "@/hooks/use-favicon"
import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion"
import { queryConfig } from "@/lib/react-query"
import { getInjectedDefaultColorMode } from "@/lib/runtime-branding"
import { reportClientError } from "@/lib/telemetry"

import { ThemeProvider, useTheme } from "./theme-provider"

type ProvidersProps = {
  children: React.ReactNode
}

function FaviconUpdater() {
  useFavicon()
  return null
}

// Rendered inside ThemeProvider so Sonner follows the app's chosen theme
// (the user's light/dark choice, or OS preference under "system") instead of
// Sonner's default of always-light.
function ThemedToaster() {
  const { theme } = useTheme()
  const prefersReducedMotion = usePrefersReducedMotion()
  return (
    <Toaster
      richColors
      position="top-center"
      theme={theme}
      toastOptions={
        prefersReducedMotion
          ? undefined
          : {
              style: {
                animation: "toast-spring-in 0.4s var(--ease-spring)",
              },
            }
      }
    />
  )
}

export function AppProvider({ children }: ProvidersProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: queryConfig,
        // Global error telemetry for every query/mutation failure. Additive —
        // local onError handlers still run; `reportClientError` dedups so an
        // error that also hits a boundary emits once. Never throws.
        queryCache: new QueryCache({
          onError: (error) => {
            reportClientError(error, { source: "react-query" })
          },
        }),
        mutationCache: new MutationCache({
          onError: (error) => {
            reportClientError(error, { source: "react-query" })
          },
        }),
      })
  )
  return (
    <QueryClientProvider client={queryClient}>
      <QueryErrorBoundary>
        {/* The deployment's default (Admin → Branding, #3298) only reaches users
            with no stored choice: ThemeProvider reads `vite-ui-theme` first. */}
        <ThemeProvider
          defaultTheme={getInjectedDefaultColorMode() ?? "light"}
          storageKey="vite-ui-theme"
        >
          <FaviconUpdater />
          <RoutesProvider>
            <MicrosoftAuthProvider>
              <HeavyPreviewLegacySeed />
              <TooltipProvider delayDuration={700}>
                <SidebarProvider>{children}</SidebarProvider>
              </TooltipProvider>
              <ThemedToaster />
            </MicrosoftAuthProvider>
          </RoutesProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </ThemeProvider>
      </QueryErrorBoundary>
    </QueryClientProvider>
  )
}
